import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Dialog, DialogActions } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { countItems, deleteRoom } from '../db/rooms';
import type { Room } from '../db/schema';

/**
 * Names the room and the count, because "are you sure?" doesn't tell anyone
 * what they're about to lose. The confirm button is the only --oxblood in the
 * app.
 */
export function DeleteRoomDialog({
  room,
  onClose,
  onDeleted,
}: {
  room: Room;
  onClose: () => void;
  onDeleted: (room: Room) => void;
}) {
  const count = useLiveQuery(() => countItems(room.id), [room.id]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function confirm() {
    setBusy(true);
    setError(undefined);
    try {
      await deleteRoom(room.id);
      onDeleted(room);
    } catch {
      setError("That room couldn't be deleted. Nothing was removed.");
      setBusy(false);
    }
  }

  return (
    <Dialog title={question(room.name, count)} onClose={onClose}>
      <p>
        Its items and every photo in them go too. This can&rsquo;t be undone.
      </p>

      {error !== undefined && (
        <p className="dialog__error" role="alert">
          {error}
        </p>
      )}

      <DialogActions>
        <Button variant="quiet" onClick={onClose} disabled={busy}>
          Keep It
        </Button>
        <Button variant="destructive" onClick={() => void confirm()} disabled={busy}>
          Delete Room
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function question(name: string, count: number | undefined): string {
  if (count === undefined || count === 0) return `Delete ${name}?`;
  return `Delete ${name} and its ${count} ${count === 1 ? 'item' : 'items'}?`;
}
